import {Component, OnInit} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UiAlertService } from '../../../services/ui-alert/ui-alert.service';
import { SpinnerService } from '../../../services/spinner/spinner.service';
import {
  PayrollProviderConnectionStatusRow,
  PayrollProviderConnectionStatusSummary,
  PayrollService,
  UkgStatusRefreshResult,
  UkgStatusSummary,
} from '../../../services/payroll/payroll.service';
import {SnackBarComponent} from '../../snackbar/snackbar.component';
import {AppSettings} from '../../../app.settings';
import { ManagedFeatureFlagsService } from '../../../services/managed-feature-flags/managed-feature-flags.service';
import { SessionService } from '../../../services/session/session.service';

type CleanupLevel = 'users' | 'teams' | 'companies';

interface UkgCleanupOptions {
  level: CleanupLevel;
  dryRun: boolean;
  deleteUserAccounts: boolean;
  forceDeleteUsers: boolean;
  onlyPendingInviteUsers: boolean;
  includeNonUkgCompanies: boolean;
}

interface UkgCompanyOption {
  id: string;
  name: string;
}

@Component({
  selector: 'app-payroll-vendors',
  templateUrl: './payroll-vendors.component.html',
  styleUrls: ['./payroll-vendors.component.scss']
})
export class PayrollVendorComponent implements OnInit {
  protected isRunningCleanup = false;
  protected cleanupResult: any = null;
  protected cleanupError = '';
  protected cleanupRunAt: Date | null = null;
  protected limitToSelectedCompanies = false;
  protected selectedCleanupCompanyIds: string[] = [];
  protected manualCleanupCompanyIds = '';
  protected excludedUserEmailsInput = '';
  protected hideNoActionCompanies = false;
  protected ukgCompanies: UkgCompanyOption[] = [];
  protected ukgStatusSummary: UkgStatusSummary | null = null;
  protected ukgStatusError = '';
  protected isRefreshingUkgStatus = false;
  protected ukgStatusRefreshMessage = '';
  protected ukgStatusRefreshAt: Date | null = null;
  protected providerConnectionStatusSummary: PayrollProviderConnectionStatusSummary | null = null;
  protected providerConnectionStatusError = '';
  protected isRefreshingProviderConnectionStatus = false;
  protected readonly isUkgCleanupDisabledInProduction = this.isProductionEnvironment();
  protected isUkgCleanupVisible = false;
  protected readonly cleanupLevelOptions = [
    {
      value: 'users' as CleanupLevel,
      label: 'Users',
      description: 'Remove team memberships for matched teams.',
    },
    {
      value: 'teams' as CleanupLevel,
      label: 'Teams',
      description: 'Delete matched teams and clean payroll team mappings.',
    },
    {
      value: 'companies' as CleanupLevel,
      label: 'Companies',
      description: 'Delete company data after team cleanup.',
    },
  ];

  private readonly defaultCleanupOptions: UkgCleanupOptions = {
    level: 'teams',
    dryRun: true,
    deleteUserAccounts: false,
    forceDeleteUsers: false,
    onlyPendingInviteUsers: true,
    includeNonUkgCompanies: false,
  };
  protected cleanupOptions: UkgCleanupOptions = {...this.defaultCleanupOptions};

  constructor(
      private snackBar: MatSnackBar,
      private spinnerService: SpinnerService,
      private uiAlertService: UiAlertService,
      private payrollService: PayrollService,
      private managedFeatureFlagsService: ManagedFeatureFlagsService,
      private sessionService: SessionService,
  ) {}

  ngOnInit(): void {
    this.refreshProviderConnectionStatusSummary();
    this.refreshUkgStatusSummary();
    this.loadUkgCleanupVisibility();
  }

  protected async refreshProviderConnectionStatusSummary(): Promise<void> {
    this.isRefreshingProviderConnectionStatus = true;
    this.providerConnectionStatusError = '';

    try {
      this.providerConnectionStatusSummary = await this.payrollService.getPayrollProviderConnectionStatusSummary();
    } catch (error: any) {
      this.providerConnectionStatusSummary = null;
      this.providerConnectionStatusError = error?.error?.error
        || error?.error?.message
        || error?.message
        || 'Unable to load payroll provider connection status.';
    } finally {
      this.isRefreshingProviderConnectionStatus = false;
    }
  }

  protected async refreshUkgStatusSummary(): Promise<void> {
    this.isRefreshingUkgStatus = true;
    this.ukgStatusError = '';

    try {
      this.ukgStatusSummary = await this.payrollService.getUkgStatusSummary();
    } catch (error: any) {
      this.ukgStatusSummary = null;
      this.ukgStatusError = error?.error?.error || error?.error?.message || error?.message || 'Unable to load UKG sandbox status.';
    } finally {
      this.isRefreshingUkgStatus = false;
    }
  }

  protected async recheckUkgStatus(scope: 'impacted' | 'all'): Promise<void> {
    this.isRefreshingUkgStatus = true;
    this.ukgStatusError = '';
    this.ukgStatusRefreshMessage = '';

    try {
      const result = await this.payrollService.refreshUkgStatusSummary(scope);
      this.applyUkgStatusRefreshResult(result);
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: this.ukgStatusRefreshMessage,
      });
    } catch (error: any) {
      this.ukgStatusError = error?.error?.error || error?.error?.message || error?.message || 'Unable to re-check UKG sandbox status.';
    } finally {
      this.isRefreshingUkgStatus = false;
    }
  }

  private applyUkgStatusRefreshResult(result: UkgStatusRefreshResult): void {
    this.ukgStatusSummary = result?.summary || null;
    this.ukgStatusRefreshAt = result?.refreshedAt ? new Date(result.refreshedAt) : new Date();

    const scopeLabel = result?.scope === 'all' ? 'all UKG companies' : 'impacted companies';
    this.ukgStatusRefreshMessage = `Re-checked ${result?.companiesChecked || 0} ${scopeLabel}. `
      + `Healthy: ${result?.companiesSucceeded || 0}, failed: ${result?.companiesFailed || 0}.`;
  }

  private reloadUkgCompanies(): void {
    this.payrollService.getUkgCompanies().then(companies => {
      this.ukgCompanies = (companies || [])
        .map((company: any) => ({
          id: company?.id,
          name: company?.name || company?.id || 'Unnamed Company',
        }))
        .filter((company: UkgCompanyOption) => !!company.id)
        .sort((a: UkgCompanyOption, b: UkgCompanyOption) => a.name.localeCompare(b.name));
    }).catch(() => {
      this.ukgCompanies = [];
    });
  }

  private async loadUkgCleanupVisibility(): Promise<void> {
    try {
      if (!this.sessionService.isExactSuperAdmin()) {
        this.isUkgCleanupVisible = false;
        return;
      }

      this.isUkgCleanupVisible = await this.managedFeatureFlagsService.isEnabled(
        'UKG_DATA_CLEANUP',
        'team-admin',
        {
          userId: this.sessionService.getUserId(),
        },
      );
    } catch (error) {
      console.error('Failed to evaluate UKG cleanup flag', error);
      this.isUkgCleanupVisible = false;
    }

    if (this.isUkgCleanupVisible && !this.isUkgCleanupDisabledInProduction) {
      this.reloadUkgCompanies();
    }
  }

  protected resetCleanupForm(): void {
    this.cleanupOptions = { ...this.defaultCleanupOptions };
    this.limitToSelectedCompanies = false;
    this.selectedCleanupCompanyIds = [];
    this.manualCleanupCompanyIds = '';
    this.excludedUserEmailsInput = '';
    this.cleanupError = '';
  }

  protected async runCleanup(forceDryRun = false): Promise<void> {
    if (!this.canUseUkgCleanup()) {
      const message = 'Exact superadmin permission and the UKG data cleanup flag are required.';
      this.cleanupError = message;
      this.snackBar.openFromComponent(SnackBarComponent, { data: message });
      return;
    }

    if (this.isUkgCleanupDisabledInProduction) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'UKG data cleanup is disabled in production.',
      });
      return;
    }

    const payload = this.buildCleanupPayload(forceDryRun);
    const dryRun = !!payload.dryRun;

    if (this.limitToSelectedCompanies && (!payload.companyIds || payload.companyIds.length === 0)) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Select at least one company or add manual company IDs.',
      });
      return;
    }

    if (!dryRun) {
      const confirmed = await this.uiAlertService.presentAlertConfirm(
        this.buildCleanupConfirmationMessage(
          payload.companyIds || [],
          payload.excludedUserEmails || [],
        ),
      );
      if (!confirmed) {
        return;
      }
    }

    this.cleanupError = '';
    this.isRunningCleanup = true;
    this.spinnerService.show(dryRun ? 'Running UKG cleanup dry run...' : 'Running UKG cleanup...');
    try {
      const result = await this.payrollService.resetUkgDataForAllCompanies(payload);
      this.cleanupResult = result;
      this.cleanupRunAt = new Date();
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: this.buildCleanupSuccessMessage(result, dryRun),
      });
      this.reloadUkgCompanies();
    } catch (error: any) {
      const message = this.getCleanupErrorMessage(error);
      this.cleanupError = message;
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: message,
      });
    } finally {
      this.spinnerService.hide();
      this.isRunningCleanup = false;
    }
  }

  private canUseUkgCleanup(): boolean {
    try {
      return this.isUkgCleanupVisible && this.sessionService.isExactSuperAdmin();
    } catch {
      return false;
    }
  }

  protected getCleanupPrimaryActionLabel(): string {
    return this.cleanupOptions.dryRun ? 'Run Dry Run' : 'Execute Cleanup';
  }

  protected trackByCleanupCompany(index: number, company: any): string {
    return company?.companyId || company?.id || `${index}`;
  }

  protected trackByCleanupUser(index: number, detail: any): string {
    return detail?.userId || `${index}`;
  }

  protected trackByCleanupTeamDetail(index: number, teamDetail: any): string {
    return teamDetail?.teamId ? `${teamDetail.teamId}-${index}` : `${index}`;
  }

  protected getCleanupTeamActionLabel(teamDetail: any): string {
    const action = `${teamDetail?.action || ''}`;
    if (action === 'would_delete') {
      return 'Would Delete';
    }
    if (action === 'deleted') {
      return 'Deleted';
    }
    if (action === 'failed') {
      return 'Delete Failed';
    }
    if (action === 'missing_already') {
      return 'Missing Team';
    }
    if (action === 'memberships_only') {
      return 'Memberships Only';
    }
    return action || 'Unknown';
  }

  protected getCleanupUserActionLabel(detail: any): string {
    const action = `${detail?.action || ''}`;
    if (action === 'would_delete') {
      return 'Would Delete';
    }
    if (action === 'deleted') {
      return 'Deleted';
    }
    if (action === 'failed') {
      return 'Failed';
    }
    if (action === 'skipped') {
      return 'Skipped';
    }
    return action || 'Unknown';
  }

  protected hasUserCleanupDetails(result: any): boolean {
    return this.getVisibleUserCleanupCompanies(result).length > 0;
  }

  protected getVisibleCompanyDetails(result: any): any[] {
    const companies = Array.isArray(result?.companies) ? result.companies : [];
    if (!this.hideNoActionCompanies) {
      return companies;
    }
    return companies.filter((company: any) => this.companyHasCleanupAction(company));
  }

  protected getVisibleUserCleanupCompanies(result: any): any[] {
    const companies = Array.isArray(result?.companies) ? result.companies : [];
    const companiesWithUserCleanup = companies.filter(
      (company: any) => (company?.userCleanup?.details || []).length > 0,
    );
    if (!this.hideNoActionCompanies) {
      return companiesWithUserCleanup;
    }
    return companiesWithUserCleanup.filter((company: any) => this.userCleanupCompanyHasAction(company));
  }

  protected formatCleanupTeamMembership(membership: any): string {
    const teamName = membership?.teamName ? `${membership.teamName}` : 'Unknown Team';
    const teamId = membership?.teamId ? `${membership.teamId}` : 'unknown-team-id';
    const role = membership?.role ? `${membership.role}` : 'member';
    return `${teamName} (${teamId}) as ${role}`;
  }

  protected formatCleanupCompanyRole(companyRole: any): string {
    const companyName = companyRole?.companyName ? `${companyRole.companyName}` : 'Unknown Company';
    const companyId = companyRole?.companyId ? `${companyRole.companyId}` : 'unknown-company-id';
    const role = companyRole?.role ? `${companyRole.role}` : 'unknown-role';
    return `${companyName} (${companyId}) as ${role}`;
  }

  protected formatCleanupVendorProduct(vendorProduct: any): string {
    const vendorName = vendorProduct?.vendorName ? `${vendorProduct.vendorName}` : 'Unknown Vendor';
    const productName = vendorProduct?.productName ? `${vendorProduct.productName}` : 'Unknown Product';
    const vendorProductId = vendorProduct?.vendorProductId ? `${vendorProduct.vendorProductId}` : 'unknown-vendor-product-id';
    const records = Number(vendorProduct?.records || 0);
    const recordLabel = records === 1 ? 'record' : 'records';
    return `${vendorName} / ${productName} (${vendorProductId}) [${records} ${recordLabel}]`;
  }

  protected getInitialTeamMemberships(detail: any): any[] {
    const initialMemberships = Array.isArray(detail?.initialRemainingTeamMemberships)
      ? detail.initialRemainingTeamMemberships
      : [];
    if (initialMemberships.length > 0) {
      return initialMemberships;
    }
    return Array.isArray(detail?.remainingTeamMemberships) ? detail.remainingTeamMemberships : [];
  }

  protected getInitialCompanyRoles(detail: any): any[] {
    const initialCompanyRoles = Array.isArray(detail?.initialRemainingCompanyRoles)
      ? detail.initialRemainingCompanyRoles
      : [];
    if (initialCompanyRoles.length > 0) {
      return initialCompanyRoles;
    }
    return Array.isArray(detail?.remainingCompanyRoles) ? detail.remainingCompanyRoles : [];
  }

  private buildCleanupPayload(forceDryRun: boolean): Record<string, any> {
    const selectedCompanyIds = this.limitToSelectedCompanies ? this.selectedCleanupCompanyIds : [];
    const manualCompanyIds = this.parseManualCompanyIds(this.manualCleanupCompanyIds);
    const companyIds = Array.from(new Set([...(selectedCompanyIds || []), ...manualCompanyIds]));
    const excludedUserEmails = this.parseExcludedUserEmails(this.excludedUserEmailsInput);

    return {
      level: this.cleanupOptions.level,
      dryRun: forceDryRun ? true : this.cleanupOptions.dryRun,
      deleteUserAccounts: this.cleanupOptions.deleteUserAccounts,
      forceDeleteUsers: this.cleanupOptions.deleteUserAccounts && this.cleanupOptions.forceDeleteUsers,
      onlyPendingInviteUsers: this.cleanupOptions.onlyPendingInviteUsers,
      includeNonUkgCompanies: this.cleanupOptions.includeNonUkgCompanies,
      ...(companyIds.length > 0 ? { companyIds } : {}),
      ...(this.cleanupOptions.deleteUserAccounts && excludedUserEmails.length > 0 ? { excludedUserEmails } : {}),
    };
  }

  private parseManualCompanyIds(rawInput: string): string[] {
    return Array.from(new Set(
      (rawInput || '')
        .split(/[\n,]+/g)
        .map(item => item.trim())
        .filter(item => !!item),
    ));
  }

  private parseExcludedUserEmails(rawInput: string): string[] {
    return Array.from(new Set(
      (rawInput || '')
        .split(/[\n,]+/g)
        .map((item) => item.trim().toLowerCase())
        .filter((item) => !!item),
    ));
  }

  private buildCleanupConfirmationMessage(companyIds: string[], excludedUserEmails: string[] = []): string {
    const scope = this.cleanupOptions.level.toUpperCase();
    const target = companyIds.length > 0
      ? `${companyIds.length} selected compan${companyIds.length === 1 ? 'y' : 'ies'}`
      : 'all UKG companies';
    const userCleanupWarning = this.cleanupOptions.deleteUserAccounts
      ? ' This run also allows user account deletion when the backend orphan checks pass.'
      : '';
    const forceDeleteWarning = this.cleanupOptions.deleteUserAccounts && this.cleanupOptions.forceDeleteUsers
      ? ' Force-delete is enabled and will also try to remove remaining team memberships and company roles before user deletion.'
      : '';
    const exclusionWarning = this.cleanupOptions.deleteUserAccounts && excludedUserEmails.length > 0
      ? ` ${excludedUserEmails.length} excluded user email${excludedUserEmails.length === 1 ? '' : 's'} will be skipped.`
      : '';

    return `Execute UKG cleanup (${scope}) for ${target}?${userCleanupWarning}${forceDeleteWarning}${exclusionWarning}`;
  }

  private companyHasCleanupAction(company: any): boolean {
    const teamMembershipsRemoved = Number(company?.teamMembershipsRemoved || 0);
    const teamsDeleted = Number(company?.teamsDeleted || 0);
    const teamDeleteFailures = Number(company?.teamDeleteFailures || 0);
    const companyUsersDeleted = Number(company?.companyUsersDeleted || 0);
    const insuredStatesDeleted = Number(company?.insuredStatesDeleted || 0);
    const companyDeleted = !!company?.companyDeleted;
    const hasCompanyErrors = (company?.errors || []).length > 0;
    const hasTeamErrors = (company?.teamCleanupDetails || []).some((detail: any) => (detail?.errors || []).length > 0);

    if (
      teamMembershipsRemoved > 0
      || teamsDeleted > 0
      || teamDeleteFailures > 0
      || companyUsersDeleted > 0
      || insuredStatesDeleted > 0
      || companyDeleted
      || hasCompanyErrors
      || hasTeamErrors
    ) {
      return true;
    }

    return this.userCleanupCompanyHasAction(company);
  }

  private userCleanupCompanyHasAction(company: any): boolean {
    const details = company?.userCleanup?.details || [];
    return details.some((detail: any) => {
      const action = `${detail?.action || ''}`.toLowerCase();
      return action === 'would_delete' || action === 'deleted' || action === 'failed';
    });
  }

  private buildCleanupSuccessMessage(result: any, dryRun: boolean): string {
    const companiesProcessed = result?.companiesProcessed ?? 0;
    const teamsDeleted = result?.totals?.teamsDeleted ?? 0;
    const membershipsRemoved = result?.totals?.teamMembershipsRemoved ?? 0;
    const companiesDeleted = result?.totals?.companiesDeleted ?? 0;
    const usersDeleted = result?.totals?.usersDeleted ?? 0;
    const usersForceDeleted = result?.totals?.usersForceDeleted ?? 0;

    if (dryRun) {
      return `Dry run complete. Companies: ${companiesProcessed}, teams: ${teamsDeleted}, memberships: ${membershipsRemoved}.`;
    }

    return `Cleanup complete. Companies deleted: ${companiesDeleted}, teams deleted: ${teamsDeleted}, users deleted: ${usersDeleted}, forced user deletes: ${usersForceDeleted}.`;
  }

  private getCleanupErrorMessage(error: any): string {
    return error?.error?.error || error?.error?.message || error?.message || 'Unable to run UKG cleanup.';
  }

  private isProductionEnvironment(): boolean {
    const environment = `${AppSettings.getEnv() || ''}`.toLowerCase();
    return environment === 'prod' || environment === 'production';
  }

  protected trackByProviderConnectionStatus(index: number, provider: PayrollProviderConnectionStatusRow): string {
    return provider?.providerSlug || `${index}`;
  }

  protected getProviderStatusLabel(status: PayrollProviderConnectionStatusRow['status'] | undefined): string {
    if (status === 'active') {
      return 'Active';
    }
    if (status === 'inactive') {
      return 'Inactive';
    }
    if (status === 'needs_credentials') {
      return 'Needs Credentials';
    }
    if (status === 'partially_unavailable') {
      return 'Partially Unavailable';
    }
    return 'Not Configured';
  }

  protected getProviderStatusPillClass(status: PayrollProviderConnectionStatusRow['status'] | undefined): string {
    if (status === 'active') {
      return 'provider-status-pill--healthy';
    }
    if (status === 'inactive' || status === 'not_configured') {
      return 'provider-status-pill--neutral';
    }
    if (status === 'needs_credentials' || status === 'partially_unavailable') {
      return 'provider-status-pill--warning';
    }
    return 'provider-status-pill--neutral';
  }

  protected getConnectionStatusSummary(provider: PayrollProviderConnectionStatusRow): string {
    const counts = provider?.connectionStatusCounts || {};
    return Object.keys(counts)
      .sort()
      .map((status) => `${status}: ${counts[status]}`)
      .join(', ') || '---';
  }

  protected getUkgStatusLabel(state: UkgStatusSummary['overallState'] | undefined): string {
    if (state === 'outage') {
      return 'Outage';
    }
    if (state === 'degraded') {
      return 'Degraded';
    }
    if (state === 'credentials_issue') {
      return 'Credentials Issue';
    }
    if (state === 'unknown') {
      return 'Unknown';
    }
    if (state === 'not_configured') {
      return 'Not Configured';
    }
    return 'Healthy';
  }

  protected getUkgStatusPillClass(state: UkgStatusSummary['overallState'] | undefined): string {
    if (state === 'healthy') {
      return 'ukg-status-pill--healthy';
    }
    if (state === 'not_configured') {
      return 'ukg-status-pill--neutral';
    }
    if (state === 'credentials_issue' || state === 'unknown') {
      return 'ukg-status-pill--warning';
    }
    return 'ukg-status-pill--error';
  }

  protected getUkgOutageAge(summary: UkgStatusSummary | null): string {
    const startedAt = new Date(summary?.currentIncidentStartedAt || '').getTime();
    if (!Number.isFinite(startedAt) || startedAt <= 0) {
      return '---';
    }

    const diffMs = Math.max(0, Date.now() - startedAt);
    const totalMinutes = Math.floor(diffMs / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;

    const parts: string[] = [];
    if (days > 0) {
      parts.push(`${days}d`);
    }
    if (hours > 0 || days > 0) {
      parts.push(`${hours}h`);
    }
    parts.push(`${minutes}m`);
    return parts.join(' ');
  }

}
